import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import {
    forwardRef,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { FRAME_EVENT } from "../consts/Frame";
import { ModalEvent } from "../consts/Modal";
import EventManager from "../EventManager/EventManager";
import { getModalDetails } from "./ModalManager.helper";

const eventManager = EventManager.getInstance();

const SlideInTransition = forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function ModalManager() {
    const [modalState, setModalState] = useState({
        open: false,
        type: "",
        props: {
            disableBackdropClose: false,
        },
        requestId: "",
    });

    const dimissModal = useCallback(() => {
        setModalState({
            open: false,
            type: "",
            props: {
                disableBackdropClose: false,
            },
            requestId: "",
        });
    }, [setModalState]);

    useEffect(() => {
        const unsubscribe = eventManager.subscribe((type, data, requestId) => {
            switch (type) {
                case FRAME_EVENT.SHOW_MODAL: {
                    setModalState({
                        open: true,
                        type: data?.type || "",
                        props: data?.props || {},
                        requestId: requestId,
                    });
                    break;
                }

                case FRAME_EVENT.RESET:
                case FRAME_EVENT.DIMISS_MODAL: {
                    dimissModal();
                    break;
                }
            }

            return unsubscribe;
        });
    }, [dimissModal]);

    /**
     * Generic Handler to communicate back to Originating Frame Component regarding the action
     */
    const sendDataToRequestingFrame = useCallback(
        ({ type, data }) => {
            eventManager.respondToRequest(modalState.requestId, {
                type,
                data,
            });
        },
        [modalState.requestId],
    );

    /**
     * User Action Handler
     */
    const handleUserAction = useCallback(
        (data) => {
            sendDataToRequestingFrame({
                type: ModalEvent.UserDataAction,
                data,
            });
        },
        [sendDataToRequestingFrame],
    );

    /**
     * Close Handler
     */
    const handleClose = useCallback(() => {
        if (modalState?.props?.disableBackdropClose) return;
        dimissModal();
        sendDataToRequestingFrame({ type: ModalEvent.Dismissed, data: null });
    }, [sendDataToRequestingFrame, dimissModal]);

    const { dialogProps, content } = useMemo(
        () =>
            getModalDetails({
                type: modalState.type,
                props: modalState.props,
                onAction: handleUserAction,
                onClose: handleClose,
            }),
        [modalState.type, modalState.props, handleUserAction, handleClose],
    );

    const dialogStyle = {
        zIndex: 999999,
    };

    return (
        <Dialog
            {...dialogProps}
            open={modalState.open}
            onClose={handleClose}
            TransitionComponent={SlideInTransition}
            sx={dialogStyle}
        >
            <Suspense fallback={null}>{content}</Suspense>
        </Dialog>
    );
}
